import React, { useRef } from 'react';
import { TextField } from '@mui/material';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Controller } from 'react-hook-form';

const googleMapKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
// Google Maps libraries - must be constant to prevent reloads
const GOOGLE_MAPS_LIBRARIES = ["places"];

const AddressAutocompleteField = ({
    control,
    name = "physicalAddress",
    label = "Physical Address",
    placeholder = "Enter your physical address",
    rules = { required: 'Physical address is required' },
    errors = {},
    sx = {},
    onAddressSelect = null // Optional callback for when address is selected
}) => {
    const autocompleteRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: googleMapKey,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const onPlaceChanged = (field) => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place.formatted_address) {
                // Update the form field
                field.onChange(place.formatted_address);

                // Optional callback with detailed location data
                if (onAddressSelect && place.geometry) {
                    // Extract country information from address components
                    const addressComponents = place.address_components || [];
                    const countryComponent = addressComponents.find(
                        (component) => component.types.includes("country")
                    );
                    const iso2 = countryComponent?.short_name || null;
                    const countryName = countryComponent?.long_name || null;
                    
                    // Extract other useful components
                    const postalCodeComponent = addressComponents.find(
                        (component) => component.types.includes("postal_code")
                    );
                    const postalCode = postalCodeComponent?.long_name || null;
                    
                    const administrativeAreaComponent = addressComponents.find(
                        (component) => component.types.includes("administrative_area_level_1")
                    );
                    const state = administrativeAreaComponent?.long_name || null;
                    const stateCode = administrativeAreaComponent?.short_name || null;
                    
                    const localityComponent = addressComponents.find(
                        (component) => component.types.includes("locality")
                    );
                    const city = localityComponent?.long_name || null;

                    let object = {
                        latitude: place.geometry.location.lat(),
                        longitude: place.geometry.location.lng(),
                        physicalAddress: place.formatted_address,
                        countryName: countryName,
                        iso2: iso2,
                        postalCode: postalCode,
                        state: state,
                        stateCode: stateCode,
                        city: city,
                        addressComponents: addressComponents,
                    }
                    onAddressSelect(object);
                }
            }
        }
    };

    if (loadError) {
        return (
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        label={label}
                        placeholder={placeholder}
                        error={!!errors[name]}
                        helperText={errors[name]?.message || "Error loading address suggestions"}
                        sx={sx}
                    />
                )}
            />
        );
    }

    if (!isLoaded) {
        return (
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        label={label}
                        placeholder="Loading address search..."
                        error={!!errors[name]}
                        helperText={errors[name]?.message}
                        sx={sx}
                        disabled
                    />
                )}
            />
        );
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
                <Autocomplete
                    onLoad={(ref) => (autocompleteRef.current = ref)}
                    onPlaceChanged={() => onPlaceChanged(field)}
                    options={{
                        types: ['address'], // Restrict to addresses only
                        componentRestrictions: { country: 'in' }, // Optional: restrict to India
                    }}
                >
                    <TextField
                        {...field}
                        fullWidth
                        label={label}
                        placeholder={placeholder}
                        error={!!errors[name]}
                        helperText={errors[name]?.message}
                        sx={sx}
                    />
                </Autocomplete>
            )}
        />
    );
};

export default AddressAutocompleteField;